'use client';

import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';

import { fetchBlogs } from '@eliadmin/core/src/api/blogs/blogs-api';
import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';
import { BlogCard } from '@eliadmin/core/src/components/blog/blog-card';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';

export const BlogsList = () => {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const searchParams = useSearchParams();
  const [categoryFilter, setCategoryFilter] = useState<number | null>(null);

  // Listen for changes in the `category` search parameter
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    setCategoryFilter(categoryParam ? parseInt(categoryParam, 10) : null);
  }, [searchParams]);

  const blogsQuery = useSuspenseInfiniteQuery({
    queryKey: ['blogs', categoryFilter],
    queryFn: ({ pageParam = 1 }) =>
      fetchBlogs({
        locale,
        query: {
          page: pageParam,
          orderBy: 'id',
          order: 'desc',
          results: 5,
          where: categoryFilter ? { categories: { in: [categoryFilter] } } : undefined,
        },
      }),
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
  });

  const blogs = blogsQuery.data?.pages.flatMap((page: any) => page.data).slice(1) ?? [];

  return (
    <div className="flex w-full flex-col items-start justify-start gap-8">
      {blogs?.map((blog: Blog) => <BlogCard blog={blog} key={blog.slug} />)}
      <div className="mt-8 flex w-full items-center justify-center">
        <Button
          onClick={() => blogsQuery.fetchNextPage()}
          isLoading={blogsQuery.isFetchingNextPage}
          className={cn({
            hidden: !blogsQuery.hasNextPage,
          })}
        >
          {t('text3904')}
        </Button>
      </div>
    </div>
  );
};
